import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "../Logo";

const NAV = [
  { label: "Products", to: "/products" },
  { label: "Solutions", to: "/solutions" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "About", to: "/about" },
];

export default function MarketingLayout({ children, hideStickyCta = false }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-alto-black text-white font-sans flex flex-col">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="AllToMAIDIt home"><Logo dark size={32} /></Link>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-alto-red" : "text-neutral-300 hover:text-white"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/app/login" data-testid="nav-sign-in-cta" className="text-sm font-semibold text-neutral-300 hover:text-white px-3 py-2">SIGN IN</Link>
            <Link to="/lead-system-audit" data-testid="nav-primary-cta" className="btn-red text-sm font-bold px-4 py-2.5 rounded-md inline-flex items-center gap-2">
              GET A FREE AUDIT <ArrowRight size={16} />
            </Link>
          </div>
          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/10 bg-black px-4 py-4 space-y-2">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-2 text-neutral-200" data-testid={`nav-mobile-${n.label.toLowerCase()}`}>{n.label}</Link>
            ))}
            <button onClick={() => { setOpen(false); navigate("/app/login"); }} className="block w-full text-left py-2 text-neutral-200">SIGN IN</button>
            <Link to="/lead-system-audit" onClick={() => setOpen(false)} className="btn-red block text-center font-bold px-4 py-3 rounded-md mt-2">GET A FREE AUDIT</Link>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {!hideStickyCta && (
        <Link to="/lead-system-audit" data-testid="sticky-mobile-cta"
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 btn-red text-center font-bold py-4 text-sm border-t border-red-900/50">
          GET YOUR FREE LEAD SYSTEM AUDIT
        </Link>
      )}

      <footer className="border-t border-white/10 bg-black pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <Logo dark size={32} />
            <p className="text-sm text-neutral-400 mt-4 leading-relaxed">From first click to closed customer. We build the system that captures your leads and makes sure somebody follows up.</p>
          </div>
          <FooterCol title="Solutions" links={[["Lead-to-Client System", "/solutions/lead-to-client"], ["All Solutions", "/solutions"], ["Free Lead System Audit", "/lead-system-audit"]]} />
          <FooterCol title="Products" links={[["AllToMAIDIt CRM", "/products/crm"], ["All Products", "/products"], ["Services", "/services"], ["Industries", "/industries"]]} />
          <FooterCol title="Company" links={[["About", "/about"], ["Sign In", "/app/login"], ["Get a Free Audit", "/lead-system-audit"]]} />
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-neutral-500">
          © {new Date().getFullYear()} AllToMAIDIt Solutions. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="eyebrow text-neutral-500 mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map(([label, to]) => (
          <li key={to}><Link to={to} className="text-sm text-neutral-300 hover:text-alto-red transition-colors">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
